import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  Users,
  DollarSign,
  TrendingUp,
  Leaf,
  Briefcase,
  Globe,
  Heart,
  Building
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

const FunderDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching funder data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate sector distribution from projects
  const sectorDistribution = projects.reduce((acc, p) => {
    const sector = p.sector || 'Other';
    acc[sector] = (acc[sector] || 0) + (p.budget_total || 0);
    return acc;
  }, {});

  const sectorDistributionOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' },
      formatter: (params) => `${params.name}: $${(params.value / 1000000).toFixed(0)}M (${params.percent}%)`
    },
    legend: {
      orient: 'vertical',
      right: '5%',
      top: 'center',
      textStyle: { color: '#94a3b8' }
    },
    series: [{
      type: 'pie',
      radius: ['45%', '75%'],
      center: ['35%', '50%'],
      avoidLabelOverlap: false,
      label: { show: false },
      emphasis: {
        label: { show: true, fontSize: 14, fontWeight: 'bold', color: '#f8fafc' }
      },
      labelLine: { show: false },
      data: Object.entries(sectorDistribution).map(([name, value]) => ({ name, value })),
      itemStyle: {
        borderRadius: 6,
        borderColor: '#0b1c2d',
        borderWidth: 3
      },
      color: ['#c9a227', '#22c55e', '#3b82f6', '#ef4444', '#a855f7', '#f97316', '#06b6d4', '#ec4899']
    }]
  };

  const esgRadarOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    radar: {
      indicator: [
        { name: 'Environmental', max: 100 },
        { name: 'Social', max: 100 },
        { name: 'Governance', max: 100 }
      ],
      axisName: { color: '#94a3b8', fontSize: 14 },
      splitLine: { lineStyle: { color: '#1e293b' } },
      splitArea: { areaStyle: { color: ['rgba(30, 41, 59, 0.3)', 'rgba(30, 41, 59, 0.5)'] } }
    },
    series: [{
      type: 'radar',
      data: [
        {
          value: [82, 75, 88],
          name: 'ESG Score',
          areaStyle: { color: 'rgba(34, 197, 94, 0.4)' },
          lineStyle: { color: '#22c55e', width: 3 },
          itemStyle: { color: '#22c55e' }
        }
      ]
    }]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading funder dashboard...</div>
      </div>
    );
  }

  // Calculate KPIs from projects
  const totalBudget = projects.reduce((acc, p) => acc + (p.budget_total || 0), 0);
  const totalSpent = projects.reduce((acc, p) => acc + (p.budget_spent || 0), 0);
  const avgCompletion = projects.length > 0 
    ? Math.round(projects.reduce((acc, p) => acc + (p.completion_percentage || 0), 0) / projects.length) 
    : 0;

  const formatCurrency = (val) => {
    if (!val) return '$0';
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  return (
    <div className="space-y-6" data-testid="funder-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Funder-Facing Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Impact visibility and financial transparency for institutional funders</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Investment Portfolio</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{formatCurrency(totalBudget)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Capital Deployed</p>
                <p className="text-3xl font-bold text-blue-400 mt-2">{formatCurrency(totalSpent)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Avg Completion</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{avgCompletion}%</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Active Projects</p>
                <p className="text-3xl font-bold text-emerald-400 mt-2">{projects.filter(p => p.status === 'active').length}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                <Leaf className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Impact Highlights */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Impact Highlights</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-gradient-to-br from-green-500/10 to-green-500/5 rounded-sm border border-green-500/20">
              <Users className="w-12 h-12 text-green-400 mx-auto" />
              <p className="text-4xl font-bold text-green-400 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {(Math.floor(totalBudget / 100000)).toLocaleString()}
              </p>
              <p className="text-slate-400 mt-2">Jobs Created</p>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-sm border border-blue-500/20">
              <Heart className="w-12 h-12 text-blue-400 mx-auto" />
              <p className="text-4xl font-bold text-blue-400 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {(projects.length * 125000).toLocaleString()}
              </p>
              <p className="text-slate-400 mt-2">People Reached</p>
            </div>
            <div className="text-center p-6 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-sm border border-purple-500/20">
              <Building className="w-12 h-12 text-purple-400 mx-auto" />
              <p className="text-4xl font-bold text-purple-400 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {projects.length * 45}
              </p>
              <p className="text-slate-400 mt-2">Communities Impacted</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Investment by Sector</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={sectorDistributionOptions} style={{ height: '350px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">ESG Scoring Grid</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={esgRadarOptions} style={{ height: '350px' }} />
          </CardContent>
        </Card>
      </div>

      {/* Projects Performance */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Portfolio Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projects?.map((project) => (
              <div 
                key={project.id} 
                className="p-4 bg-slate-800/30 rounded-sm cursor-pointer hover:bg-slate-800/50 transition-colors"
                onClick={() => navigate(`/projects/${project.id}`)}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#c9a227]" />
                    <div>
                      <p className="font-medium text-white">{project.name}</p>
                      <p className="text-xs text-slate-500">{project.country} • {project.sector}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#c9a227] font-semibold">{formatCurrency(project.budget_total)}</p>
                    <Badge className={(project.completion_percentage || 0) >= 50 ? 'status-green' : 'status-yellow'}>
                      Progress: {project.completion_percentage || 0}%
                    </Badge>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Progress</p>
                    <Progress value={project.completion_percentage || 0} className="h-2 mt-1 bg-slate-700" />
                  </div>
                  <div>
                    <p className="text-slate-500">ESG</p>
                    <p className="text-emerald-400 font-semibold">{project.esg_score || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Jobs</p>
                    <p className="text-blue-400 font-semibold">{(project.jobs_created || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Compliance</p>
                    <p className="text-green-400 font-semibold">{project.compliance_score || 'N/A'}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FunderDashboard;
